/**
 * The editable surface of the model.
 *
 * Every number a run depends on is described here: what it means in plain language, what
 * range is sensible, what unit it is in, and — the field that matters most — whether it is
 * something you could go and measure or something we made up.
 *
 * This registry exists because the interesting use of this simulator is not watching the
 * scenarios we shipped. It is substituting your own numbers. A researcher with survey data
 * on strike-fund coverage, or employment shares for a particular country and year, or a
 * measured distribution of protest thresholds, should be able to find the parameter that
 * corresponds to their data, see what we currently have there and on what basis, and
 * replace it. The `evidence` field tells them which of our numbers are worth arguing with
 * and which are placeholders waiting for someone who knows better.
 *
 * The interface builds its editors from this file rather than hard-coding controls, so a
 * parameter added here becomes editable everywhere without any UI work.
 */

import { DEFAULT_TUNABLES, TUNABLE_META } from './constants.ts';
import type { Tunables } from './types.ts';

/** Which part of a configuration a parameter belongs to. */
export type ParameterScope = 'sector' | 'pillar' | 'movement' | 'media' | 'regime' | 'tunable';

/**
 * How much of a claim on reality a parameter makes.
 *
 * - `measurable`  official statistics exist. If ours disagree with yours, yours are
 *                 probably right, and we would like the pull request.
 * - `estimable`   no direct measurement, but survey or case-study evidence constrains it.
 *                 Reasonable people will disagree by a factor of two, not ten.
 * - `judgement`   nobody has measured this and nobody can. It is a modelling assumption.
 *                 Treat any specific value as a question, not an answer.
 */
export type Evidence = 'measurable' | 'estimable' | 'judgement';

export type ParameterUnit =
  /** A fraction of some population or total, 0-1. Displayed as a percentage. */
  | 'share'
  /** A count of days. */
  | 'days'
  /** A dimensionless 0-1 weight with no external referent. */
  | 'index';

export interface ParameterMeta {
  /** Path within its scope. Dotted for nested fields, e.g. `threshold.mean`. */
  path: string;
  scope: ParameterScope;
  label: string;
  /** What this means, written for someone who has not read the source. */
  description: string;
  unit: ParameterUnit;
  min: number;
  max: number;
  step: number;
  evidence: Evidence;
  /** What kind of data would pin this down, for anyone substituting their own. */
  sourcedBy: string;
}

// ---------------------------------------------------------------------------
// Sectors
// ---------------------------------------------------------------------------

export const SECTOR_PARAMETERS: ParameterMeta[] = [
  {
    path: 'size',
    scope: 'sector',
    label: 'Share of workforce',
    description:
      'How much of the working population is in this sector. Sizes are renormalised to sum to 100%, so you can enter rough shares.',
    unit: 'share',
    min: 0,
    max: 1,
    step: 0.005,
    evidence: 'measurable',
    sourcedBy:
      'National statistics office employment by industry, or ILOSTAT. Record the year — sector shares move a lot over a decade.',
  },
  {
    path: 'bufferDays',
    scope: 'sector',
    label: 'Days of savings',
    description:
      'How long a typical member can go without income before being forced back to work. The single most common reason real general strikes end.',
    unit: 'days',
    min: 0,
    max: 365,
    step: 1,
    evidence: 'estimable',
    sourcedBy:
      'Household financial-resilience surveys ("could you cover an unexpected expense / how long could you live on savings"), union strike-fund rules, or central bank household finance data.',
  },
  {
    path: 'bufferSpread',
    scope: 'sector',
    label: 'Inequality of savings',
    description:
      'How unequally those savings are distributed within the sector, as a fraction of the average. High values mean some members run out in days while others last months.',
    unit: 'index',
    min: 0,
    max: 1.5,
    step: 0.05,
    evidence: 'estimable',
    sourcedBy:
      'The dispersion of the same household surveys — the ratio of the interquartile range to the median is a reasonable starting point.',
  },
  {
    path: 'riskTolerance',
    scope: 'sector',
    label: 'Tolerance for risk',
    description:
      'How much personal exposure members will accept. Low for people who are easily identified and easily replaced; higher for those with legal protection or nothing to lose.',
    unit: 'index',
    min: 0,
    max: 1,
    step: 0.05,
    evidence: 'judgement',
    sourcedBy:
      'No direct measure exists. Employment protection legislation indices, union density, and the share of workers on temporary contracts are the closest available proxies.',
  },
  {
    path: 'leverage',
    scope: 'sector',
    label: 'Economic chokepoint weight',
    description:
      'How much of the economy stops when this sector does. Ports and power stations are near the top; retail is not.',
    unit: 'index',
    min: 0,
    max: 1,
    step: 0.05,
    evidence: 'estimable',
    sourcedBy:
      'A published input-output table: the column sum of a sector’s intermediate output is a defensible basis for this.',
  },
  {
    path: 'networkDensity',
    scope: 'sector',
    label: 'Internal connectedness',
    description:
      'How quickly information travels inside the sector, and therefore how accurately members judge how many colleagues are out.',
    unit: 'index',
    min: 0,
    max: 1,
    step: 0.05,
    evidence: 'judgement',
    sourcedBy:
      'Union density and workplace concentration are the usual proxies. A genuine measure would need workplace network data.',
  },
  {
    path: 'visibility',
    scope: 'sector',
    label: 'Visibility of a stoppage',
    description:
      'How obvious it is to everyone else when this sector stops. A struck rail network is unmissable; a struck back office is invisible.',
    unit: 'index',
    min: 0,
    max: 1,
    step: 0.05,
    evidence: 'judgement',
    sourcedBy:
      'No measure exists. Media coverage volume per striker would be an interesting empirical basis and we are not aware of anyone having built it.',
  },
  {
    path: 'threshold.mean',
    scope: 'sector',
    label: 'Average joining threshold',
    description:
      'The participation level at which a typical member is willing to join — "I will walk out when this many others have". The binding constraint on most runs.',
    unit: 'share',
    min: 0,
    max: 1,
    step: 0.01,
    evidence: 'estimable',
    sourcedBy:
      'Survey questions of the form "would you take part if X% of your colleagues did" recover this distribution directly. Rare, and the best possible contribution to this project.',
  },
  {
    path: 'threshold.sd',
    scope: 'sector',
    label: 'Spread of joining thresholds',
    description:
      'How much thresholds vary within the sector. Granovetter’s central finding is that this matters more than the average does: a group with a few instigators behaves nothing like an identical group without them.',
    unit: 'share',
    min: 0.01,
    max: 0.5,
    step: 0.01,
    evidence: 'estimable',
    sourcedBy: 'The dispersion of the same survey responses.',
  },
  {
    path: 'initialPrivateSupport',
    scope: 'sector',
    label: 'Private support at day 0',
    description:
      'The share who privately want the movement to win, whatever they say or do in public. Only these people can ever participate.',
    unit: 'share',
    min: 0,
    max: 1,
    step: 0.01,
    evidence: 'measurable',
    sourcedBy:
      'Opinion polling, with the usual caveat that polls in repressive settings understate private support — which is the phenomenon this model is about.',
  },
  {
    path: 'initialParticipation',
    scope: 'sector',
    label: 'Already out at day 0',
    description:
      'The share on strike when the run begins. Granovetter’s instigators: small changes here can decide the whole run.',
    unit: 'share',
    min: 0,
    max: 1,
    step: 0.005,
    evidence: 'measurable',
    sourcedBy: 'Union strike ballots and reported turnout on the first day of a real campaign.',
  },
  {
    path: 'inputDependence',
    scope: 'sector',
    label: 'Dependence on other sectors',
    description:
      'How much this sector needs inputs from elsewhere to keep working. High for manufacturing, which stops when the freight stops; low for education, which does not.',
    unit: 'index',
    min: 0,
    max: 1,
    step: 0.05,
    evidence: 'estimable',
    sourcedBy:
      'A published input-output table: the row sum of a sector’s intermediate inputs as a share of its gross output.',
  },
];

// ---------------------------------------------------------------------------
// Pillars of support
// ---------------------------------------------------------------------------

export const PILLAR_PARAMETERS: ParameterMeta[] = [
  {
    path: 'loyalty',
    scope: 'pillar',
    label: 'Loyalty at day 0',
    description:
      'How committed this institution is to the current order. Falls under pressure; recovers when the pressure comes off; below 20% the institution stops obeying and does not come back.',
    unit: 'index',
    min: 0,
    max: 1,
    step: 0.05,
    evidence: 'judgement',
    sourcedBy:
      'Elite surveys, defection and resignation rates in comparable episodes, and case-study judgement. Inherently contested.',
  },
  {
    path: 'weight',
    scope: 'pillar',
    label: 'Share of state capacity',
    description:
      'How much of the regime’s ability to function this institution supplies. Weights are renormalised to sum to 100%.',
    unit: 'share',
    min: 0,
    max: 1,
    step: 0.01,
    evidence: 'judgement',
    sourcedBy:
      'Budget share, headcount, and constitutional role give a defensible starting point, but this is fundamentally an analyst’s judgement about where power actually sits.',
  },
  {
    path: 'economicExposure',
    scope: 'pillar',
    label: 'Exposure to economic damage',
    description:
      'How much of the economic cost this institution personally bears. Near the top for a business elite; near the bottom for a judiciary on a salary.',
    unit: 'index',
    min: 0,
    max: 1,
    step: 0.05,
    evidence: 'estimable',
    sourcedBy: 'Ownership and revenue concentration data; who actually loses money when output stops.',
  },
  {
    path: 'breadthSensitivity',
    scope: 'pillar',
    label: 'Sensitivity to breadth',
    description:
      'How much this institution’s loyalty responds to a campaign spanning many sectors, as opposed to a large one confined to a few.',
    unit: 'index',
    min: 0,
    max: 1,
    step: 0.05,
    evidence: 'judgement',
    sourcedBy: 'No measure exists. Comparative case studies of institutional defection are the only guide.',
  },
  {
    path: 'repressionSensitivity',
    scope: 'pillar',
    label: 'Sensitivity to repression backfiring',
    description:
      'How much damage an act of repression that produces public outrage does to this institution’s loyalty. Highest for those who have to carry it out or defend it in public.',
    unit: 'index',
    min: 0,
    max: 1,
    step: 0.05,
    evidence: 'judgement',
    sourcedBy: 'Martin’s backfire case studies describe the mechanism; nobody has quantified it per institution.',
  },
  {
    path: 'cascadeInfluence',
    scope: 'pillar',
    label: 'Pull on other institutions',
    description:
      'How hard this institution’s defection drags the others after it. This term is what turns defection into a cascade rather than a series of unrelated decisions.',
    unit: 'index',
    min: 0,
    max: 1,
    step: 0.05,
    evidence: 'judgement',
    sourcedBy:
      'No measure exists. The nearest empirical handle is the observed ordering of defections in documented campaigns — which institution went first, and how quickly the rest followed — but that is a handful of cases, not a rate.',
  },
];

// ---------------------------------------------------------------------------
// Movement, media, regime
// ---------------------------------------------------------------------------

export const MOVEMENT_PARAMETERS: ParameterMeta[] = [
  {
    path: 'nonviolentDiscipline',
    scope: 'movement',
    label: 'Nonviolent discipline',
    description:
      'How consistently the campaign stays nonviolent. Below 60% repression stops backfiring and starts working; below 35% for five days the run ends as a violent escalation.',
    unit: 'index',
    min: 0,
    max: 1,
    step: 0.05,
    evidence: 'estimable',
    sourcedBy:
      'NAVCO codes campaigns as primarily violent or nonviolent; finer-grained event data (ACLED) can give a share of events involving violence.',
  },
  {
    path: 'strikeFundDays',
    scope: 'movement',
    label: 'Strike fund',
    description:
      'Extra days of income the movement can cover for every sector. The most direct thing a campaign can do about its own biggest weakness.',
    unit: 'days',
    min: 0,
    max: 120,
    step: 1,
    evidence: 'measurable',
    sourcedBy: 'Published union strike-fund rules: fund size, weekly payment, and eligible membership.',
  },
  {
    path: 'organisation',
    scope: 'movement',
    label: 'Organisational capacity',
    description:
      'The infrastructure that holds a campaign together — coordination, marshals, training. Mainly acts by defending nonviolent discipline against the strain of repression.',
    unit: 'index',
    min: 0,
    max: 1,
    step: 0.05,
    evidence: 'judgement',
    sourcedBy: 'Union density, the presence of a formal coalition structure, and prior campaign experience.',
  },
];

export const MEDIA_PARAMETERS: ParameterMeta[] = [
  {
    path: 'stateControl',
    scope: 'media',
    label: 'State or owner control of media',
    description:
      'How much of the mass media the regime can direct. Suppresses how large the campaign appears, which is the mechanism preference falsification runs on.',
    unit: 'index',
    min: 0,
    max: 1,
    step: 0.05,
    evidence: 'measurable',
    sourcedBy:
      'Media ownership monitors, Reporters Without Borders and Freedom House press-freedom indices, and state broadcaster audience share.',
  },
  {
    path: 'independentReach',
    scope: 'media',
    label: 'Reach of independent media',
    description:
      'How far independent and social media route around that control. At full reach a blackout achieves nothing.',
    unit: 'index',
    min: 0,
    max: 1,
    step: 0.05,
    evidence: 'measurable',
    sourcedBy: 'Internet and smartphone penetration, VPN usage, and independent outlet audience share.',
  },
];

export const REGIME_PARAMETERS: ParameterMeta[] = [
  {
    path: 'initialRepression',
    scope: 'regime',
    label: 'Repression at day 0',
    description: 'How hard the state is already leaning on people when the campaign starts.',
    unit: 'index',
    min: 0,
    max: 1,
    step: 0.05,
    evidence: 'estimable',
    sourcedBy: 'Political terror scale, CIRI physical integrity rights index, or V-Dem civil liberties indicators.',
  },
  {
    path: 'repressionCapacity',
    scope: 'regime',
    label: 'Willingness to escalate',
    description:
      'The hardest the regime is prepared to push, if its security forces will follow the order. A ceiling, not a plan.',
    unit: 'index',
    min: 0,
    max: 1,
    step: 0.05,
    evidence: 'judgement',
    sourcedBy: 'Prior behaviour in comparable episodes. Regimes routinely surprise analysts in both directions.',
  },
  {
    path: 'initialLegitimacy',
    scope: 'regime',
    label: 'Legitimacy at day 0',
    description: 'How much of the public accepts the regime’s right to govern, independent of the dispute.',
    unit: 'index',
    min: 0,
    max: 1,
    step: 0.05,
    evidence: 'measurable',
    sourcedBy: 'Opinion polling on trust in government, or recent election results if they were meaningful.',
  },
  {
    path: 'initialTreasury',
    scope: 'regime',
    label: 'Room to manoeuvre',
    description:
      'Fiscal and political capacity to keep acting — to pay for repression, buy people off, and absorb economic damage.',
    unit: 'index',
    min: 0,
    max: 1,
    step: 0.05,
    evidence: 'estimable',
    sourcedBy: 'Fiscal reserves, sovereign borrowing costs, and the size of any sovereign wealth fund.',
  },
  {
    path: 'intransigence',
    scope: 'regime',
    label: 'Refusal to concede',
    description:
      'How much the regime values not backing down for its own sake. High values produce a government that will burn its legitimacy and its treasury rather than give anything up.',
    unit: 'index',
    min: 0,
    max: 1,
    step: 0.05,
    evidence: 'judgement',
    sourcedBy: 'Prior negotiating behaviour, and whether conceding would threaten the leadership personally.',
  },
];

/**
 * The tunables, in the same shape as everything else.
 *
 * These are the parameters with no external referent at all — they are the model's own
 * internal rates, and every one of them is `judgement` by construction. They are exposed
 * because the honest response to a parameter nobody has measured is a slider, not a number
 * with two decimal places.
 */
export const TUNABLE_PARAMETERS: ParameterMeta[] = (
  Object.keys(DEFAULT_TUNABLES) as (keyof Tunables)[]
).map((key) => {
  const meta = TUNABLE_META[key];
  return {
    path: key,
    scope: 'tunable' as const,
    label: meta.label,
    description: meta.description,
    unit: 'index' as const,
    min: meta.min,
    max: meta.max,
    step: meta.step,
    evidence: 'judgement' as const,
    sourcedBy:
      'An internal rate of the model with no real-world counterpart. Vary it and see whether your conclusion survives; if it does not, that is the finding.',
  };
});

export const ALL_PARAMETERS: ParameterMeta[] = [
  ...SECTOR_PARAMETERS,
  ...PILLAR_PARAMETERS,
  ...MOVEMENT_PARAMETERS,
  ...MEDIA_PARAMETERS,
  ...REGIME_PARAMETERS,
  ...TUNABLE_PARAMETERS,
];

export function parametersForScope(scope: ParameterScope): ParameterMeta[] {
  return ALL_PARAMETERS.filter((p) => p.scope === scope);
}

export function findParameter(scope: ParameterScope, path: string): ParameterMeta | undefined {
  return ALL_PARAMETERS.find((p) => p.scope === scope && p.path === path);
}

// ---------------------------------------------------------------------------
// Reading and writing parameters by path
// ---------------------------------------------------------------------------

/** Read a parameter out of a config object by its dotted path. */
export function readParameter(target: unknown, path: string): number | undefined {
  let cursor: unknown = target;
  for (const key of path.split('.')) {
    if (typeof cursor !== 'object' || cursor === null) return undefined;
    cursor = (cursor as Record<string, unknown>)[key];
  }
  return typeof cursor === 'number' ? cursor : undefined;
}

/**
 * Return a copy of `target` with one parameter changed, clamped to the parameter's
 * declared range. Never mutates the original — edits have to be safe to apply to a config
 * that a run is currently using.
 */
export function writeParameter<T>(target: T, path: string, value: number, meta?: ParameterMeta): T {
  const clamped = meta ? Math.min(meta.max, Math.max(meta.min, value)) : value;
  const [head, ...rest] = path.split('.');
  if (head === undefined) return target;

  const source = target as unknown as Record<string, unknown>;
  if (rest.length === 0) {
    return { ...source, [head]: clamped } as unknown as T;
  }
  return {
    ...source,
    [head]: writeParameter(source[head] ?? {}, rest.join('.'), clamped, meta),
  } as unknown as T;
}

/**
 * Every parameter whose value is a modelling assumption rather than something measurable.
 *
 * Worth surfacing prominently: a reader deciding how much to trust a run should be able to
 * see at a glance how much of it rests on numbers nobody has ever measured.
 */
export function judgementParameters(): ParameterMeta[] {
  return ALL_PARAMETERS.filter((p) => p.evidence === 'judgement');
}
